import React, { useContext, useState } from 'react';

import { ArcaContext } from '../context/ArcaContext.context';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import { Button } from '../components/ui/button';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '../components/ui/alert-dialog';
import requestAccountDeletion from '../api/users/requestAccountDeletion';
import { esApp } from './plataforma';

type Paso = 'nada' | 'confirmar' | 'ultima' | 'enviada' | 'error';

/**
 * «Borrar mi cuenta». Apple y Google exigen que se pueda pedir desde dentro de la app.
 *
 * MVP decidido el 15-oct-2026: la petición llega a soporte por correo (comparsa
 * `POST /users/me/account-deletion-request`) y soporte la tramita a mano. Doble confirmación antes
 * de enviarla. El plan de pensiones no se cancela.
 *
 * Solo en la app: en el navegador no pinta nada.
 */
const BorrarCuenta: React.FC = () => {
	const { translate } = useContext(I18nContext).state;
	const { token } = useContext(ArcaContext);
	const [paso, setPaso] = useState<Paso>('nada');
	const [enviando, setEnviando] = useState(false);

	if (!esApp()) {
		return null;
	}

	const enviar = async () => {
		setPaso('nada');
		setEnviando(true);
		const enviada = await requestAccountDeletion(token);
		setEnviando(false);
		setPaso(enviada ? 'enviada' : 'error');
	};

	const aviso = (cual: 'enviada' | 'error') => (
		<AlertDialog open={paso === cual}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{translate(`movil.borrarCuenta.${cual}.titulo`)}</AlertDialogTitle>
					<AlertDialogDescription>
						{translate(`movil.borrarCuenta.${cual}.texto`)}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogAction onClick={() => setPaso('nada')}>
						{translate('movil.borrarCuenta.entendido')}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);

	return (
		<div className="mt-4 rounded-lg border border-destructive/20 bg-destructive/5 p-5">
			<h3 className="text-sm font-semibold text-foreground mb-2">
				{translate('movil.borrarCuenta.titulo')}
			</h3>
			<p className="text-sm text-muted-foreground mb-4">{translate('movil.borrarCuenta.texto')}</p>
			<Button
				variant="outline"
				className="border-destructive text-destructive hover:bg-destructive/10 rounded-full"
				disabled={enviando}
				onClick={() => setPaso('confirmar')}
			>
				{translate('movil.borrarCuenta.boton')}
			</Button>

			<AlertDialog open={paso === 'confirmar'}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{translate('movil.borrarCuenta.confirmar.titulo')}</AlertDialogTitle>
						<AlertDialogDescription>
							{translate('movil.borrarCuenta.confirmar.texto')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setPaso('nada')}>
							{translate('movil.borrarCuenta.confirmar.cancelar')}
						</AlertDialogCancel>
						<AlertDialogAction onClick={() => setPaso('ultima')}>
							{translate('movil.borrarCuenta.confirmar.continuar')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={paso === 'ultima'}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{translate('movil.borrarCuenta.ultima.titulo')}</AlertDialogTitle>
						<AlertDialogDescription>
							{translate('movil.borrarCuenta.ultima.texto')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setPaso('nada')}>
							{translate('movil.borrarCuenta.ultima.no')}
						</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={() => void enviar()}
						>
							{translate('movil.borrarCuenta.ultima.si')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{aviso('enviada')}
			{aviso('error')}
		</div>
	);
};

export default BorrarCuenta;
